import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  Settings as SettingsIcon,
  Eye,
  FileText,
  ImagePlus,
  ChevronDown,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';
import {
  Card,
  Button,
  Input,
  Badge,
} from '@azyntor-labs/nexus-ui';
import { examsApi, questionsApi, questionBanksApi, uploadsApi } from '@/api/endpoints';
import { Exam, ExamSettings, Question, QuestionBank } from '@/types';
import { toast } from '@/utils/toast';

interface ExamEditorProps {
  viewOnly?: boolean;
}

// Convert UTC ISO string to datetime-local format in local timezone
const utcToLocal = (utcStr: string): string => {
  const d = new Date(utcStr);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const HelpTip = ({ text }: { text: string }) => (
  <div className="relative group inline-flex ml-1">
    <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-white/10 text-white/40 text-[10px] font-bold cursor-help hover:bg-white/20 hover:text-white/60 transition-colors">?</span>
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1a1a2e] border border-white/20 rounded-lg text-xs text-white/80 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl pointer-events-none">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px] w-2 h-2 bg-[#1a1a2e] border-r border-b border-white/20 rotate-45" />
    </div>
  </div>
);

export const ExamEditor = ({ viewOnly = false }: ExamEditorProps) => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isEditMode = !!examId;
  const isViewMode = viewOnly;

  const [activeTab, setActiveTab] = useState<'details' | 'questions' | 'settings'>('details');
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);
  const [selectedForRemoval, setSelectedForRemoval] = useState<Set<string>>(new Set());
  const [violationPenaltiesExpanded, setViolationPenaltiesExpanded] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    start_time: '',
    end_time: '',
    duration_minutes: 60,
    passing_score: 60,
    // Question Settings
    shuffle_questions: false,
    shuffle_options: false,
    // Result & Review Settings
    show_results: true,
    show_results_timing: 'immediate' as 'immediate' | 'after_end' | 'after_grading' | 'manual',
    allow_review: true,
    show_correct_answers: false,
    auto_grade: true,
    // Attempt Settings
    max_attempts: 1,
    allow_retake: false,
    retake_delay_hours: 0,
    // Submission Settings
    allow_late_submit: false,
    late_submit_penalty: 0,
    auto_submit_on_timeout: true,
    // Navigation Settings
    allow_backtracking: true,
    require_answer_before_next: false,
    allow_skip: true,
    show_question_navigator: true,
    // Timing
    timing_mode: 'global' as 'global' | 'per_question' | 'per_question_difficulty',
    difficulty_time_map: { easy: 20, medium: 40, hard: 60 },
    auto_advance_on_timeout: true,
    // Security Settings
    require_fullscreen: false,
    browser_lockdown: false,
    ip_restriction_enabled: false,
    allowed_ip_ranges: '',
    // Proctoring
    proctoring_enabled: false,
    proctoring_settings: {
      webcam_required: false,
      microphone_required: false,
      screen_recording: false,
      tab_switch_detection: false,
      copy_paste_detection: false,
      right_click_disabled: false,
      keyboard_shortcuts_disabled: false,
      face_detection: false,
      multiple_faces_detection: false,
      gaze_detection: false,
      phone_detection: false,
      vm_detection: false,
      max_violations: 25,
      warning_threshold: 5,
      auto_terminate: true,
    },
    // Grading Settings
    negative_marking: false,
    negative_marking_percentage: 25,
    partial_marking: false,
    // Certificate Settings
    certificate_enabled: false,
    certificate_config: {
      template_id: '',
      title: '',
      subtitle: '',
      show_grade: true,
      show_score: true,
      signature_name: '',
      signature_title: '',
      organization_name: '',
      anchor_to_blockchain: false,
    },
    // Plagiarism
    auto_plagiarism_check: false,
    // Messaging
    pass_message: '',
    fail_message: '',
    // Violation Penalties
    auto_penalty_enabled: false,
    violation_penalties: {
      tab_switch: { max_count: 5, action: 'warn' as const, penalty_percent: 2 },
      copy_paste: { max_count: 3, action: 'penalty' as const, penalty_percent: 5 },
      mobile_detected: { max_count: 2, action: 'auto_submit' as const, penalty_percent: 10 },
      multiple_faces: { max_count: 2, action: 'auto_submit' as const, penalty_percent: 15 },
      no_face: { max_count: 10, action: 'penalty' as const, penalty_percent: 3 },
      devtools_open: { max_count: 1, action: 'auto_submit' as const, penalty_percent: 25 },
      fullscreen_exit: { max_count: 5, action: 'warn' as const, penalty_percent: 2 },
      looking_away: { max_count: 15, action: 'warn' as const, penalty_percent: 0 },
      screenshot_attempt: { max_count: 2, action: 'auto_submit' as const, penalty_percent: 15 },
    } as Record<string, { max_count: number; action: 'warn' | 'penalty' | 'auto_submit'; penalty_percent: number }>,
  });

  // Fetch exam if editing
  const { data: examResponse, isLoading: isLoadingExam } = useQuery({
    queryKey: ['exam', examId],
    queryFn: () => examsApi.get(examId!),
    enabled: isEditMode,
  });

  // Fetch exam questions if editing
  const { data: examQuestionsResponse } = useQuery({
    queryKey: ['exam-questions', examId],
    queryFn: () => examsApi.getQuestions(examId!),
    enabled: isEditMode && !!examId,
  });

  // Fetch question banks for breakdown stats
  const { data: questionBanksResponse } = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => questionBanksApi.list({ per_page: 100 }),
    enabled: selectedQuestions.length > 0,
  });

  // Create a map of question bank IDs to names
  const questionBankMap = new Map<string, QuestionBank>();
  if (questionBanksResponse?.data?.data?.data) {
    (questionBanksResponse.data.data.data as QuestionBank[]).forEach((bank) => {
      questionBankMap.set(bank.id, bank);
    });
  }

  // Calculate question breakdown stats
  const questionStats = {
    byDifficulty: {
      easy: selectedQuestions.filter((q) => q.difficulty === 'easy').length,
      medium: selectedQuestions.filter((q) => q.difficulty === 'medium').length,
      hard: selectedQuestions.filter((q) => q.difficulty === 'hard').length,
    },
    byBank: selectedQuestions.reduce((acc, q) => {
      const bankId = q.question_bank_id;
      if (!acc[bankId]) {
        acc[bankId] = { count: 0, bank: questionBankMap.get(bankId) };
      }
      acc[bankId].count++;
      return acc;
    }, {} as Record<string, { count: number; bank?: QuestionBank }>),
    byType: selectedQuestions.reduce((acc, q) => {
      const type = q.question_type;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  };

  // Handle questions added from AddQuestionsFromBank page
  // Wait for examQuestionsResponse to be loaded before processing to avoid race condition
  const questionsProcessedRef = useRef(false);
  useEffect(() => {
    if (questionsProcessedRef.current) return;
    if (location.state?.questionsToAdd && examId && examQuestionsResponse) {
      questionsProcessedRef.current = true;
      const newQuestions = location.state.questionsToAdd as Question[];

      // Get existing questions from the API response (not from state which might be stale)
      const responseData = examQuestionsResponse?.data;
      let existingQuestions: Question[] = [];
      if (responseData) {
        const nestedData = responseData.data;
        if (Array.isArray(nestedData)) {
          existingQuestions = nestedData;
        } else if (nestedData && Array.isArray((nestedData as { data?: Question[] }).data)) {
          existingQuestions = (nestedData as { data: Question[] }).data;
        }
      }

      // Calculate the merged questions list
      const existingIds = new Set(existingQuestions.map((q) => q.id));
      const uniqueNew = newQuestions.filter((q) => !existingIds.has(q.id));
      const mergedQuestions = [...existingQuestions, ...uniqueNew];

      // Update local state immediately
      setSelectedQuestions(mergedQuestions);

      // Clear the location state
      navigate(location.pathname, { replace: true, state: {} });

      // Auto-save the questions to the backend
      const questionIds = mergedQuestions.map(q => q.id);
      examsApi.update(examId, { question_ids: questionIds } as Partial<Exam> & { question_ids: string[] })
        .then(() => {
          toast.success(`${uniqueNew.length} question(s) added and saved (total: ${mergedQuestions.length})`);
          // Invalidate cache so exam data is refreshed
          queryClient.invalidateQueries({ queryKey: ['exam', examId] });
          queryClient.invalidateQueries({ queryKey: ['exam-questions', examId] });
        })
        .catch((error) => {
          console.error('Failed to save questions:', error);
          toast.error('Questions added locally but failed to save. Please save the exam manually.');
        });
    }
  }, [location.state, examId, examQuestionsResponse]);

  // Apply template settings from sessionStorage (set by ExamTemplatesPage)
  useEffect(() => {
    if (!isEditMode) {
      const templateData = sessionStorage.getItem('exam_template_prefill');
      if (templateData) {
        try {
          const settings = JSON.parse(templateData);
          setFormData((prev) => ({
            ...prev,
            duration_minutes: settings.duration_minutes ?? prev.duration_minutes,
            passing_score: settings.passing_score ?? prev.passing_score,
            shuffle_questions: settings.shuffle_questions ?? prev.shuffle_questions,
            shuffle_options: settings.shuffle_options ?? prev.shuffle_options,
            show_results: settings.show_results ?? prev.show_results,
            allow_review: settings.allow_review ?? prev.allow_review,
            auto_grade: settings.auto_grade ?? prev.auto_grade,
            max_attempts: settings.max_attempts ?? prev.max_attempts,
            allow_retake: settings.allow_retake ?? prev.allow_retake,
            allow_late_submit: settings.allow_late_submit ?? prev.allow_late_submit,
            auto_submit_on_timeout: settings.auto_submit_on_timeout ?? prev.auto_submit_on_timeout,
            allow_backtracking: settings.allow_backtracking ?? prev.allow_backtracking,
            require_fullscreen: settings.require_fullscreen ?? prev.require_fullscreen,
            browser_lockdown: settings.browser_lockdown ?? prev.browser_lockdown,
            proctoring_enabled: settings.proctoring_enabled ?? prev.proctoring_enabled,
            proctoring_settings: settings.proctoring_settings ?? prev.proctoring_settings,
            negative_marking: settings.negative_marking ?? prev.negative_marking,
            negative_marking_percentage: settings.negative_marking_percentage ?? prev.negative_marking_percentage,
            certificate_enabled: settings.certificate_enabled ?? prev.certificate_enabled,
            certificate_config: settings.certificate_config ?? prev.certificate_config,
            auto_plagiarism_check: settings.auto_plagiarism_check ?? prev.auto_plagiarism_check,
          }));
          sessionStorage.removeItem('exam_template_prefill');
          toast.success('Template applied successfully');
        } catch {
          sessionStorage.removeItem('exam_template_prefill');
        }
      }
    }
  }, [isEditMode]);

  // Apply clone data from sessionStorage (set by ExamManagement Clone button)
  useEffect(() => {
    if (!isEditMode) {
      const cloneData = sessionStorage.getItem('exam_clone_data');
      if (cloneData) {
        try {
          const data = JSON.parse(cloneData);
          const examSettings = data.settings || {};

          // Set form data from cloned exam
          setFormData((prev) => ({
            ...prev,
            title: data.title || prev.title,
            description: data.description || prev.description,
            instructions: data.instructions || prev.instructions,
            duration_minutes: data.duration_minutes ?? prev.duration_minutes,
            passing_score: data.passing_marks && data.total_marks
              ? Math.round((data.passing_marks / data.total_marks) * 100)
              : prev.passing_score,
            shuffle_questions: data.shuffle_questions ?? prev.shuffle_questions,
            shuffle_options: data.shuffle_options ?? prev.shuffle_options,
            show_results: data.show_results ?? prev.show_results,
            allow_review: examSettings.allow_review ?? data.show_answers ?? prev.allow_review,
            show_correct_answers: examSettings.show_correct_answers ?? prev.show_correct_answers,
            auto_grade: examSettings.auto_grade ?? prev.auto_grade,
            max_attempts: data.max_attempts ?? prev.max_attempts,
            allow_retake: examSettings.allow_retake ?? prev.allow_retake,
            allow_late_submit: data.allow_late_submit ?? prev.allow_late_submit,
            late_submit_penalty: examSettings.late_submit_penalty ?? prev.late_submit_penalty,
            auto_submit_on_timeout: examSettings.auto_submit_on_timeout ?? prev.auto_submit_on_timeout,
            allow_backtracking: examSettings.allow_backtracking ?? prev.allow_backtracking,
            require_answer_before_next: examSettings.require_answer_before_next ?? prev.require_answer_before_next,
            allow_skip: examSettings.allow_skip ?? prev.allow_skip,
            show_question_navigator: examSettings.show_question_navigator ?? prev.show_question_navigator,
            timing_mode: examSettings.timing_mode ?? prev.timing_mode,
            difficulty_time_map: examSettings.difficulty_time_map ?? prev.difficulty_time_map,
            auto_advance_on_timeout: examSettings.auto_advance_on_timeout ?? prev.auto_advance_on_timeout,
            require_fullscreen: data.require_fullscreen ?? examSettings.require_fullscreen ?? prev.require_fullscreen,
            browser_lockdown: examSettings.browser_lockdown ?? prev.browser_lockdown,
            proctoring_enabled: data.is_proctored ?? prev.proctoring_enabled,
            proctoring_settings: {
              ...prev.proctoring_settings,
              webcam_required: data.require_webcam ?? examSettings.webcam_required ?? prev.proctoring_settings.webcam_required,
              tab_switch_detection: data.detect_tab_switch ?? examSettings.tab_switch_detection ?? prev.proctoring_settings.tab_switch_detection,
              copy_paste_detection: data.detect_copy_paste ?? examSettings.copy_paste_detection ?? prev.proctoring_settings.copy_paste_detection,
              face_detection: data.ai_proctoring ?? examSettings.face_detection ?? prev.proctoring_settings.face_detection,
              ...(examSettings.max_violations ? { max_violations: examSettings.max_violations } : {}),
              ...(examSettings.warning_threshold ? { warning_threshold: examSettings.warning_threshold } : {}),
              ...(examSettings.microphone_required !== undefined ? { microphone_required: examSettings.microphone_required } : {}),
              ...(examSettings.screen_recording !== undefined ? { screen_recording: examSettings.screen_recording } : {}),
              ...(examSettings.right_click_disabled !== undefined ? { right_click_disabled: examSettings.right_click_disabled } : {}),
              ...(examSettings.keyboard_shortcuts_disabled !== undefined ? { keyboard_shortcuts_disabled: examSettings.keyboard_shortcuts_disabled } : {}),
              ...(examSettings.multiple_faces_detection !== undefined ? { multiple_faces_detection: examSettings.multiple_faces_detection } : {}),
              ...(examSettings.gaze_detection !== undefined ? { gaze_detection: examSettings.gaze_detection } : {}),
              ...(examSettings.phone_detection !== undefined ? { phone_detection: examSettings.phone_detection } : {}),
              ...(examSettings.vm_detection !== undefined ? { vm_detection: examSettings.vm_detection } : {}),
              ...(examSettings.auto_terminate !== undefined ? { auto_terminate: examSettings.auto_terminate } : {}),
            },
            negative_marking: examSettings.negative_marking ?? prev.negative_marking,
            negative_marking_percentage: examSettings.negative_marking_percentage ?? prev.negative_marking_percentage,
            auto_penalty_enabled: examSettings.auto_penalty_enabled ?? prev.auto_penalty_enabled,
            violation_penalties: examSettings.violation_penalties ?? prev.violation_penalties,
            certificate_enabled: data.certificate_enabled ?? prev.certificate_enabled,
            certificate_config: data.certificate_config ?? prev.certificate_config,
            auto_plagiarism_check: examSettings.auto_plagiarism_check ?? prev.auto_plagiarism_check,
            pass_message: examSettings.pass_message ?? prev.pass_message,
            fail_message: examSettings.fail_message ?? prev.fail_message,
          }));

          // Load cloned questions
          if (data.question_ids && data.question_ids.length > 0) {
            // Fetch full question data for the cloned question IDs
            examsApi.getQuestions(data.question_ids[0]).catch(() => null); // warm cache
            // We need to fetch questions by IDs — use the questions API
            Promise.all(
              data.question_ids.map((id: string) =>
                questionsApi.get(id).then(res => res?.data?.data).catch(() => null)
              )
            ).then(questions => {
              const validQuestions = questions.filter(Boolean);
              if (validQuestions.length > 0) {
                setSelectedQuestions(validQuestions);
              }
            });
          }

          sessionStorage.removeItem('exam_clone_data');
        } catch {
          sessionStorage.removeItem('exam_clone_data');
        }
      }
    }
  }, [isEditMode]);

  // Load exam data for editing
  useEffect(() => {

    if (examResponse?.data.data) {
      const exam = examResponse.data.data;
      const settings = exam.settings || {}; // Extended settings stored in settings JSON


      // Calculate passing score percentage from passing marks and total marks
      const passingScorePercentage = exam.total_marks > 0
        ? Math.round((exam.passing_marks / exam.total_marks) * 100)
        : 60;

      // Build proctoring settings from exam data and settings JSON
      const proctoringSettings = {
        webcam_required: exam.require_webcam || settings.webcam_required || false,
        microphone_required: settings.microphone_required || false,
        screen_recording: settings.screen_recording || false,
        tab_switch_detection: exam.detect_tab_switch || settings.tab_switch_detection || false,
        copy_paste_detection: exam.detect_copy_paste || settings.copy_paste_detection || false,
        right_click_disabled: settings.right_click_disabled || false,
        keyboard_shortcuts_disabled: settings.keyboard_shortcuts_disabled || false,
        face_detection: exam.ai_proctoring || settings.face_detection || false,
        multiple_faces_detection: settings.multiple_faces_detection || false,
        gaze_detection: settings.gaze_detection || false,
        phone_detection: settings.phone_detection || false,
        vm_detection: settings.vm_detection || false,
        max_violations: settings.max_violations || 25,
        warning_threshold: settings.warning_threshold || 5,
        auto_terminate: settings.auto_terminate !== false, // default true
      };

      const newFormData = {
        title: exam.title || '',
        description: exam.description || '',
        instructions: exam.instructions || '',
        start_time: exam.start_time ? utcToLocal(exam.start_time) : '',
        end_time: exam.end_time ? utcToLocal(exam.end_time) : '',
        duration_minutes: exam.duration || exam.duration_minutes || 60,
        passing_score: passingScorePercentage,
        // Question Settings
        shuffle_questions: exam.shuffle_questions || false,
        shuffle_options: exam.shuffle_options || false,
        // Result & Review Settings
        show_results: exam.show_results || false,
        show_results_timing: settings.show_results_timing || 'immediate',
        allow_review: exam.show_answers || settings.allow_review || false,
        show_correct_answers: settings.show_correct_answers || false,
        auto_grade: settings.auto_grade !== false, // default true
        // Attempt Settings
        max_attempts: exam.max_attempts || 1,
        allow_retake: settings.allow_retake || false,
        retake_delay_hours: settings.retake_delay_hours || 0,
        // Submission Settings
        allow_late_submit: exam.allow_late_submit || false,
        late_submit_penalty: settings.late_submit_penalty || 0,
        auto_submit_on_timeout: settings.auto_submit_on_timeout !== false, // default true
        // Navigation Settings
        allow_backtracking: settings.allow_backtracking !== false, // default true
        require_answer_before_next: settings.require_answer_before_next || false,
        allow_skip: settings.allow_skip !== false,
        show_question_navigator: settings.show_question_navigator !== false,
        timing_mode: settings.timing_mode || 'global',
        difficulty_time_map: settings.difficulty_time_map || { easy: 20, medium: 40, hard: 60 },
        auto_advance_on_timeout: settings.auto_advance_on_timeout !== false,
        // Security Settings
        require_fullscreen: exam.require_fullscreen || false,
        browser_lockdown: settings.browser_lockdown || false,
        ip_restriction_enabled: settings.ip_restriction_enabled || false,
        allowed_ip_ranges: settings.allowed_ip_ranges || '',
        // Proctoring
        proctoring_enabled: exam.is_proctored || false,
        proctoring_settings: proctoringSettings,
        // Grading Settings
        negative_marking: settings.negative_marking || false,
        negative_marking_percentage: settings.negative_marking_percentage || 25,
        partial_marking: settings.partial_marking || false,
        // Certificate Settings
        certificate_enabled: exam.certificate_enabled || false,
        certificate_config: exam.certificate_config || {
          template_id: '',
          title: '',
          subtitle: '',
          show_grade: true,
          show_score: true,
          signature_name: '',
          signature_title: '',
          organization_name: '',
          anchor_to_blockchain: false,
        },
        // Plagiarism
        auto_plagiarism_check: settings.auto_plagiarism_check || false,
        // Messaging
        pass_message: settings.pass_message || '',
        fail_message: settings.fail_message || '',
        // Violation Penalties
        auto_penalty_enabled: settings.auto_penalty_enabled || false,
        violation_penalties: settings.violation_penalties || {
          tab_switch: { max_count: 5, action: 'warn', penalty_percent: 2 },
          copy_paste: { max_count: 3, action: 'penalty', penalty_percent: 5 },
          mobile_detected: { max_count: 2, action: 'auto_submit', penalty_percent: 10 },
          multiple_faces: { max_count: 2, action: 'auto_submit', penalty_percent: 15 },
          no_face: { max_count: 10, action: 'penalty', penalty_percent: 3 },
          devtools_open: { max_count: 1, action: 'auto_submit', penalty_percent: 25 },
          fullscreen_exit: { max_count: 5, action: 'warn', penalty_percent: 2 },
          looking_away: { max_count: 15, action: 'warn', penalty_percent: 0 },
          screenshot_attempt: { max_count: 2, action: 'auto_submit', penalty_percent: 15 },
        },
      };

      setFormData(newFormData as typeof formData);
    }
  }, [examResponse]);

  // Load selected questions separately
  useEffect(() => {

    if (examQuestionsResponse?.data) {
      // Handle different response structures - the API might return data directly or nested
      const responseData = examQuestionsResponse.data as { data?: { data?: Question[] } | Question[] };

      // Extract the questions data - could be in data.data or just data
      const nestedData = responseData.data;
      let loadedQuestions: Question[] = [];

      if (Array.isArray(nestedData)) {
        loadedQuestions = nestedData;
      } else if (nestedData && Array.isArray((nestedData as { data?: Question[] }).data)) {
        loadedQuestions = (nestedData as { data: Question[] }).data;
      }

      setSelectedQuestions(loadedQuestions);
    }
  }, [examQuestionsResponse]);

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<Exam>) => {
      if (isEditMode) {
        return examsApi.update(examId!, data);
      }
      return examsApi.create(data);
    },
    onSuccess: (response) => {
      // Invalidate all related caches
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      queryClient.invalidateQueries({ queryKey: ['exam', examId] });
      queryClient.invalidateQueries({ queryKey: ['exam-questions', examId] });
      toast.success(isEditMode ? 'Exam updated successfully' : 'Exam created successfully');

      // If creating a new exam, navigate to edit page; if editing, stay on current page
      if (!isEditMode && response?.data?.data?.id) {
        navigate(`/teacher/exams/${response.data.data.id}/edit`);
      }
      // If editing, stay on current page (don't navigate)
    },
    onError: (error: any) => {
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
      const message = serverMessage || (error instanceof Error ? error.message : 'Failed to save exam');
      toast.error(message);
    },
  });

  // Question image upload
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [uploadingQuestionId, setUploadingQuestionId] = useState<string | null>(null);

  const questionImageMutation = useMutation({
    mutationFn: async ({ questionId, file }: { questionId: string; file: File }) => {
      const response = await uploadsApi.uploadImage(file);
      const url = (response.data as any)?.data?.url;
      if (url) {
        setSelectedQuestions((prev) =>
          prev.map((q) => q.id === questionId ? { ...q, image_url: url } : q)
        );
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Image attached to question');
      setUploadingQuestionId(null);
    },
    onError: () => {
      toast.error('Failed to upload image');
      setUploadingQuestionId(null);
    },
  });

  const handleQuestionImageUpload = (questionId: string) => {
    setUploadingQuestionId(questionId);
    imageInputRef.current?.click();
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingQuestionId) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      setUploadingQuestionId(null);
      return;
    }
    questionImageMutation.mutate({ questionId: uploadingQuestionId, file });
    e.target.value = '';
  };

  const handleSubmit = (status: 'draft' | 'scheduled') => {
    // Validation
    if (!formData.title || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.duration_minutes || formData.duration_minutes <= 0) {
      toast.error('Duration must be greater than 0');
      return;
    }

    if (!formData.passing_score || formData.passing_score <= 0 || formData.passing_score > 100) {
      toast.error('Passing score must be between 1 and 100');
      return;
    }

    const totalPoints = selectedQuestions.reduce((sum, q) => sum + q.points, 0);

    // Convert local datetime-local value to UTC ISO string
    const formatDateTime = (dateTimeLocal: string) => {
      if (!dateTimeLocal) return '';
      // datetime-local input gives local time: "2025-11-11T12:31"
      // new Date() treats it as local, toISOString() converts to UTC
      return new Date(dateTimeLocal).toISOString();
    };

    // Calculate passing marks from percentage
    const passingMarks = (formData.passing_score / 100) * totalPoints;

    // Get question IDs
    const questionIds = selectedQuestions.map(q => q.id);


    // Transform field names to match backend API
    // Build extended settings object for all additional options
    const extendedSettings = {
      // Proctoring settings
      ...formData.proctoring_settings,
      // Result & Review Settings
      show_results_timing: formData.show_results_timing,
      show_correct_answers: formData.show_correct_answers,
      auto_grade: formData.auto_grade,
      // Attempt Settings
      allow_retake: formData.allow_retake,
      retake_delay_hours: formData.retake_delay_hours,
      // Submission Settings
      late_submit_penalty: formData.late_submit_penalty,
      auto_submit_on_timeout: formData.auto_submit_on_timeout,
      // Navigation Settings
      allow_backtracking: formData.allow_backtracking,
      require_answer_before_next: formData.require_answer_before_next,
      allow_skip: formData.allow_skip,
      show_question_navigator: formData.show_question_navigator,
      // Timing
      timing_mode: formData.timing_mode,
      difficulty_time_map: formData.timing_mode === 'per_question_difficulty' ? formData.difficulty_time_map : undefined,
      auto_advance_on_timeout: formData.auto_advance_on_timeout,
      // Security Settings
      browser_lockdown: formData.browser_lockdown,
      ip_restriction_enabled: formData.ip_restriction_enabled,
      allowed_ip_ranges: formData.allowed_ip_ranges,
      // Grading Settings
      negative_marking: formData.negative_marking,
      negative_marking_percentage: formData.negative_marking_percentage,
      partial_marking: formData.partial_marking,
      // Certificate Settings (kept in settings for backward compat)
      certificate_enabled: formData.certificate_enabled,
      // Plagiarism
      auto_plagiarism_check: formData.auto_plagiarism_check,
      // Messaging
      pass_message: formData.pass_message,
      fail_message: formData.fail_message,
      // Violation Penalties
      auto_penalty_enabled: formData.auto_penalty_enabled,
      violation_penalties: formData.violation_penalties,
    };

    const examData: Partial<Exam> & { question_ids: string[] } = {
      title: formData.title,
      description: formData.description || undefined,
      instructions: formData.instructions || undefined,
      duration_minutes: (() => {
        if (formData.timing_mode === 'per_question') {
          const totalSec = selectedQuestions.reduce((s, q) => s + ((q as any).time_limit_seconds || 60), 0);
          return Math.ceil(totalSec / 60);
        }
        if (formData.timing_mode === 'per_question_difficulty') {
          const map = formData.difficulty_time_map;
          const totalSec = selectedQuestions.reduce((s, q) => s + (map[(q.difficulty || 'medium') as keyof typeof map] || map.medium), 0);
          return Math.ceil(totalSec / 60);
        }
        return Number(formData.duration_minutes);
      })(),
      total_marks: totalPoints,
      passing_marks: passingMarks,
      start_time: formatDateTime(formData.start_time),
      end_time: formatDateTime(formData.end_time),
      status,
      question_ids: questionIds,
      is_proctored: formData.proctoring_enabled,
      allow_late_submit: formData.allow_late_submit,
      shuffle_questions: formData.shuffle_questions,
      shuffle_options: formData.shuffle_options,
      show_results: formData.show_results,
      show_answers: formData.allow_review,
      max_attempts: formData.max_attempts,
      require_webcam: formData.proctoring_settings.webcam_required,
      require_fullscreen: formData.require_fullscreen,
      detect_tab_switch: formData.proctoring_settings.tab_switch_detection,
      detect_copy_paste: formData.proctoring_settings.copy_paste_detection,
      ai_proctoring: formData.proctoring_settings.face_detection,
      certificate_enabled: formData.certificate_enabled,
      certificate_config: formData.certificate_enabled ? formData.certificate_config : undefined,
      settings: extendedSettings as unknown as ExamSettings,
    } as any;

    saveMutation.mutate(examData, {
      onSuccess: () => {
        // Save per-question time limits when in per_question mode
        if (formData.timing_mode === 'per_question') {
          selectedQuestions.forEach((q) => {
            const timeLimit = (q as any).time_limit_seconds;
            if (timeLimit && timeLimit > 0) {
              questionsApi.update(q.id, { time_limit_seconds: timeLimit } as any).catch(() => {
                // Best-effort — don't block exam save
              });
            }
          });
        }
      },
    });
  };

  // Selection for bulk removal
  const toggleQuestionForRemoval = (questionId: string) => {
    const newSet = new Set(selectedForRemoval);
    if (newSet.has(questionId)) {
      newSet.delete(questionId);
    } else {
      newSet.add(questionId);
    }
    setSelectedForRemoval(newSet);
  };

  const selectAllForRemoval = () => {
    const allIds = new Set(selectedQuestions.map((q) => q.id));
    setSelectedForRemoval(allIds);
  };

  const deselectAllForRemoval = () => {
    setSelectedForRemoval(new Set());
  };

  const handleBulkRemove = () => {
    if (selectedForRemoval.size === 0) {
      toast.error('No questions selected for removal');
      return;
    }

    const updatedQuestions = selectedQuestions.filter(q => !selectedForRemoval.has(q.id));
    const removedCount = selectedForRemoval.size;

    setSelectedQuestions(updatedQuestions);
    setSelectedForRemoval(new Set());

    // Save the updated question list to the backend
    if (examId) {
      const questionIds = updatedQuestions.map(q => q.id);
      examsApi.update(examId, { question_ids: questionIds } as Partial<Exam> & { question_ids: string[] })
        .then(() => {
          toast.success(`${removedCount} question(s) removed and saved`);
          queryClient.invalidateQueries({ queryKey: ['exam', examId] });
          queryClient.invalidateQueries({ queryKey: ['exam-questions', examId] });
        })
        .catch((error) => {
          console.error('Failed to save after removing questions:', error);
          toast.error('Questions removed locally but failed to save. Please save the exam manually.');
          // Revert the local state on error
          setSelectedQuestions(selectedQuestions);
        });
    } else {
      toast.success(`${removedCount} question(s) removed`);
    }
  };

  const handleRemoveQuestion = (questionId: string) => {
    const updatedQuestions = selectedQuestions.filter(q => q.id !== questionId);
    setSelectedQuestions(updatedQuestions);

    // Save the updated question list to the backend
    if (examId) {
      const questionIds = updatedQuestions.map(q => q.id);
      examsApi.update(examId, { question_ids: questionIds } as Partial<Exam> & { question_ids: string[] })
        .then(() => {
          toast.success('Question removed and saved');
          queryClient.invalidateQueries({ queryKey: ['exam', examId] });
          queryClient.invalidateQueries({ queryKey: ['exam-questions', examId] });
        })
        .catch((error) => {
          console.error('Failed to save after removing question:', error);
          toast.error('Question removed locally but failed to save. Please save the exam manually.');
          // Revert the local state on error
          setSelectedQuestions(selectedQuestions);
        });
    } else {
      toast.success('Question removed');
    }
  };

  const totalPoints = selectedQuestions.reduce((sum, q) => sum + q.points, 0);

  const DEFAULT_VIOLATION_PENALTIES: Record<string, { label: string; penalty_percent: number; max_count: number; action: 'warn' | 'penalty' | 'auto_submit' }> = {
    tab_switch:         { label: 'Tab Switch',               penalty_percent: 2,  max_count: 5,  action: 'warn'        },
    copy_paste:         { label: 'Copy / Paste',             penalty_percent: 5,  max_count: 3,  action: 'penalty'     },
    mobile_detected:    { label: 'Phone/Mobile Detected (AI)', penalty_percent: 10, max_count: 2,  action: 'auto_submit' },
    multiple_faces:     { label: 'Multiple Faces (AI)',       penalty_percent: 15, max_count: 2,  action: 'auto_submit' },
    no_face:            { label: 'No Face Detected (AI)',     penalty_percent: 3,  max_count: 10, action: 'penalty'     },
    devtools_open:      { label: 'DevTools Open',            penalty_percent: 25, max_count: 1,  action: 'auto_submit' },
    fullscreen_exit:    { label: 'Fullscreen Exit',          penalty_percent: 2,  max_count: 5,  action: 'warn'        },
    looking_away:       { label: 'Looking Away (AI)',        penalty_percent: 0,  max_count: 15, action: 'warn'        },
    screenshot_attempt: { label: 'Screenshot Attempt',       penalty_percent: 15, max_count: 2,  action: 'auto_submit' },
  };

  if (isLoadingExam) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-white/50">Loading exam...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden file input for question images */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageFileChange}
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate('/teacher/exams')}
          >
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isViewMode ? 'View Exam' : isEditMode ? 'Edit Exam' : 'Create New Exam'}
            </h1>
            <p className="text-white/50 mt-1">
              {isViewMode
                ? 'Review exam details and questions'
                : isEditMode
                ? 'Update exam details and questions'
                : 'Set up a new exam for your students'}
            </p>
          </div>
        </div>
        {!isViewMode && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => handleSubmit('draft')}
              disabled={saveMutation.isPending}
            >
              Save as Draft
            </Button>
            <Button
              leftIcon={<Save className="w-4 h-4" />}
              onClick={() => handleSubmit('scheduled')}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : isEditMode ? 'Update Exam' : 'Save & Schedule'}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-white/[0.06]">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'details'
                ? 'border-teal-500 text-teal-400'
                : 'border-transparent text-white/50 hover:text-white hover:border-white/30'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Exam Details
          </button>
          <button
            onClick={() => setActiveTab('questions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'questions'
                ? 'border-teal-500 text-teal-400'
                : 'border-transparent text-white/50 hover:text-white hover:border-white/30'
            }`}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Questions ({selectedQuestions.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'settings'
                ? 'border-teal-500 text-teal-400'
                : 'border-transparent text-white/50 hover:text-white hover:border-white/30'
            }`}
          >
            <SettingsIcon className="w-4 h-4 inline mr-2" />
            Settings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-white mb-4">Basic Information</h2>
              <div className="space-y-4">
                <Input
                  label="Exam Title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Midterm Exam - Physics"
                  disabled={isViewMode}
                />
                <Input
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the exam"
                  disabled={isViewMode}
                />
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1.5">
                    Instructions
                  </label>
                  <textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    rows={4}
                    className="input-dark w-full"
                    placeholder="Provide instructions for students taking this exam..."
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-white mb-4">Schedule</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Start Date & Time"
                  type="datetime-local"
                  required
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                />
                <Input
                  label="End Date & Time"
                  type="datetime-local"
                  required
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                />
                <Input
                  label="Duration (minutes)"
                  type="number"
                  required
                  value={formData.duration_minutes}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setFormData({ ...formData, duration_minutes: value });
                  }}
                  min="1"
                />
              </div>

              {/* Timing Mode */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-white/70 mb-2">Question Timing <HelpTip text="Choose how time is allocated: Global gives one timer for the entire exam. Per Question sets individual time limits. By Difficulty auto-assigns time based on question difficulty level." /></label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {([
                    { value: 'global', label: 'Global Timer', desc: 'Single timer for entire exam' },
                    { value: 'per_question', label: 'Per Question', desc: 'Set time per question manually' },
                    { value: 'per_question_difficulty', label: 'By Difficulty', desc: 'Auto-assign by easy/medium/hard' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`p-3 rounded-lg border text-left transition-all ${
                        formData.timing_mode === opt.value
                          ? 'bg-teal-500/10 border-teal-500/50 ring-1 ring-teal-500/30'
                          : 'bg-white/5 border-white/[0.06] hover:border-white/20'
                      }`}
                      onClick={() => setFormData({ ...formData, timing_mode: opt.value })}
                      disabled={isViewMode}
                    >
                      <p className={`font-medium text-sm ${formData.timing_mode === opt.value ? 'text-teal-400' : 'text-white'}`}>
                        {opt.label}
                      </p>
                      <p className="text-xs text-white/50 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Difficulty time map */}
                {formData.timing_mode === 'per_question_difficulty' && (
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    {(['easy', 'medium', 'hard'] as const).map((level) => (
                      <div key={level}>
                        <label className="block text-xs font-medium text-white/50 mb-1 capitalize">{level} (seconds)</label>
                        <input
                          type="number"
                          min="5"
                          max="600"
                          value={formData.difficulty_time_map[level]}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setFormData({
                              ...formData,
                              difficulty_time_map: {
                                ...formData.difficulty_time_map,
                                [level]: raw === '' ? '' as any : parseInt(raw) || 0,
                              },
                            });
                          }}
                          onBlur={(e) => {
                            const val = parseInt(e.target.value);
                            setFormData({
                              ...formData,
                              difficulty_time_map: {
                                ...formData.difficulty_time_map,
                                [level]: isNaN(val) || val < 5 ? 20 : Math.min(val, 600),
                              },
                            });
                          }}
                          className="w-full bg-white/[0.06] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-teal-500/60"
                          disabled={isViewMode}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Per-question time info */}
                {formData.timing_mode === 'per_question' && selectedQuestions.length > 0 && (
                  <p className="mt-3 text-xs text-white/50">
                    Set time per question in the Questions list below. Total: {(() => {
                      const totalSec = selectedQuestions.reduce((s, q) => s + ((q as any).time_limit_seconds || 60), 0);
                      const mins = Math.ceil(totalSec / 60);
                      return `${totalSec}s (${mins} min)`;
                    })()}
                  </p>
                )}

                {formData.timing_mode === 'per_question_difficulty' && selectedQuestions.length > 0 && (
                  <p className="mt-3 text-xs text-white/50">
                    Auto-calculated total: {(() => {
                      const map = formData.difficulty_time_map;
                      const totalSec = selectedQuestions.reduce((s, q) => s + (map[q.difficulty as keyof typeof map] || map.medium), 0);
                      const mins = Math.ceil(totalSec / 60);
                      return `${totalSec}s (${mins} min)`;
                    })()}
                  </p>
                )}

                {/* Auto-advance toggle for per-question modes */}
                {formData.timing_mode !== 'global' && (
                  <div className="flex items-center justify-between p-3 mt-3 bg-white/5 border border-white/[0.06] rounded-lg">
                    <div>
                      <p className="font-medium text-white text-sm">Auto-Advance on Timeout <HelpTip text="Automatically move to the next question when the per-question timer expires. If disabled, the student gets a warning but stays on the question." /></p>
                      <p className="text-xs text-white/50">Move to next question when time runs out</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.auto_advance_on_timeout}
                      onChange={(e) => setFormData({ ...formData, auto_advance_on_timeout: e.target.checked })}
                      className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                      disabled={isViewMode}
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-white mb-4">Scoring</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-1.5">
                    Total Points
                  </label>
                  <div className="px-4 py-2 bg-white/5 border border-white/[0.06] rounded-lg text-white font-medium">
                    {totalPoints} points
                  </div>
                  <p className="text-xs text-white/40 mt-1">
                    Automatically calculated from selected questions
                  </p>
                </div>
                <Input
                  label="Passing Score (%)"
                  type="number"
                  required
                  value={formData.passing_score}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setFormData({ ...formData, passing_score: value });
                  }}
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'questions' && (
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">Selected Questions</h2>
                <p className="text-sm text-white/50">
                  {selectedQuestions.length} questions • {totalPoints} points total
                  {selectedForRemoval.size > 0 && (
                    <span className="text-orange-400 ml-2">
                      • {selectedForRemoval.size} selected for removal
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedQuestions.length > 0 && !isViewMode && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={selectedForRemoval.size === selectedQuestions.length ? deselectAllForRemoval : selectAllForRemoval}
                    >
                      {selectedForRemoval.size === selectedQuestions.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedForRemoval.size > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<Trash2 className="w-4 h-4" />}
                        onClick={handleBulkRemove}
                        className="text-red-400 border-red-400/50 hover:bg-red-500/10"
                      >
                        Remove Selected ({selectedForRemoval.size})
                      </Button>
                    )}
                  </>
                )}
                <Button
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => {
                    if (!examId) {
                      toast.error('Please save the exam as draft first, then you can add questions');
                      return;
                    }
                    navigate(`/teacher/exams/${examId}/add-questions`);
                  }}
                >
                  Add Questions
                </Button>
              </div>
            </div>

            {selectedQuestions.length === 0 ? (
              <div className="text-center py-12 bg-white/5 rounded-lg border-2 border-dashed border-white/[0.06]">
                <FileText className="w-12 h-12 text-white/30 mx-auto mb-2" />
                <p className="text-white/50 mb-3">No questions added yet</p>
                {!examId ? (
                  <div className="space-y-2">
                    <p className="text-sm text-white/40">Save the exam as draft first to add questions</p>
                    <Button
                      size="sm"
                      leftIcon={<Save className="w-4 h-4" />}
                      onClick={() => handleSubmit('draft')}
                      disabled={saveMutation.isPending}
                    >
                      Save as Draft
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate(`/teacher/exams/${examId}/add-questions`)}
                  >
                    Add Your First Question
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Question Breakdown Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Difficulty Breakdown */}
                  <div className="p-4 bg-white/5 rounded-lg border border-white/[0.06]">
                    <h3 className="text-sm font-medium text-white/70 mb-3">Difficulty Breakdown</h3>
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                        <span className="text-white font-medium">{questionStats.byDifficulty.easy}</span>
                        <span className="text-white/50 text-sm">Easy</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <span className="text-white font-medium">{questionStats.byDifficulty.medium}</span>
                        <span className="text-white/50 text-sm">Medium</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <span className="text-white font-medium">{questionStats.byDifficulty.hard}</span>
                        <span className="text-white/50 text-sm">Hard</span>
                      </div>
                    </div>
                    {/* Visual bar */}
                    <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden flex">
                      {questionStats.byDifficulty.easy > 0 && (
                        <div
                          className="h-full bg-green-400"
                          style={{ width: `${(questionStats.byDifficulty.easy / selectedQuestions.length) * 100}%` }}
                        />
                      )}
                      {questionStats.byDifficulty.medium > 0 && (
                        <div
                          className="h-full bg-yellow-400"
                          style={{ width: `${(questionStats.byDifficulty.medium / selectedQuestions.length) * 100}%` }}
                        />
                      )}
                      {questionStats.byDifficulty.hard > 0 && (
                        <div
                          className="h-full bg-red-400"
                          style={{ width: `${(questionStats.byDifficulty.hard / selectedQuestions.length) * 100}%` }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Question Banks Breakdown */}
                  <div className="p-4 bg-white/5 rounded-lg border border-white/[0.06]">
                    <h3 className="text-sm font-medium text-white/70 mb-3">Question Banks</h3>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {Object.entries(questionStats.byBank).map(([bankId, data]) => (
                        <div key={bankId} className="flex items-center justify-between text-sm">
                          <span className="text-white truncate flex-1 mr-2">
                            {data.bank?.name || 'Unknown Bank'}
                          </span>
                          <div className="flex items-center space-x-2">
                            <Badge variant="primary" size="sm">
                              {data.count} questions
                            </Badge>
                            {data.bank?.subject && (
                              <span className="text-white/40 text-xs">{data.bank.subject}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Question Type Breakdown */}
                {Object.keys(questionStats.byType).length > 1 && (
                  <div className="p-4 bg-white/5 rounded-lg border border-white/[0.06] mb-6">
                    <h3 className="text-sm font-medium text-white/70 mb-3">Question Types</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(questionStats.byType).map(([type, count]) => (
                        <Badge key={type} variant="secondary" size="sm">
                          {type.replace('_', ' ')}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              <div className="space-y-3">
                {selectedQuestions.map((question, index) => (
                  <div
                    key={question.id}
                    className={`p-4 bg-white/5 border rounded-lg transition-colors ${
                      selectedForRemoval.has(question.id)
                        ? 'border-orange-500/50 bg-orange-500/5'
                        : 'border-white/[0.06] hover:border-teal-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {!isViewMode && (
                        <input
                          type="checkbox"
                          checked={selectedForRemoval.has(question.id)}
                          onChange={() => toggleQuestionForRemoval(question.id)}
                          className="mt-1 w-5 h-5 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-white/50">Q{index + 1}</span>
                          <Badge variant="primary" size="sm">
                            {question.question_type.replace('_', ' ')}
                          </Badge>
                          <Badge
                            variant={
                              question.difficulty === 'easy'
                                ? 'success'
                                : question.difficulty === 'medium'
                                ? 'warning'
                                : 'danger'
                            }
                            size="sm"
                          >
                            {question.difficulty}
                          </Badge>
                          <span className="text-sm text-white/50">{question.points} pts</span>
                          {formData.timing_mode === 'per_question' && (
                            <div className="flex items-center gap-1 ml-auto">
                              <input
                                type="number"
                                min="5"
                                max="600"
                                value={(question as any).time_limit_seconds ?? 60}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const val = raw === '' ? '' : parseInt(raw);
                                  setSelectedQuestions(prev => prev.map(q =>
                                    q.id === question.id ? { ...q, time_limit_seconds: val } as any : q
                                  ));
                                }}
                                onBlur={(e) => {
                                  const val = parseInt(e.target.value);
                                  const clamped = isNaN(val) || val < 5 ? 60 : Math.min(val, 600);
                                  setSelectedQuestions(prev => prev.map(q =>
                                    q.id === question.id ? { ...q, time_limit_seconds: clamped } as any : q
                                  ));
                                }}
                                className="w-16 text-center bg-white/[0.06] border border-white/[0.10] rounded-md px-1 py-0.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-teal-500/60"
                                disabled={isViewMode}
                              />
                              <span className="text-xs text-white/40">sec</span>
                            </div>
                          )}
                        </div>
                        <p className="text-white font-medium line-clamp-2">
                          {question.question_text}
                        </p>
                        {(question as any).image_url && (
                          <img
                            src={(question as any).image_url}
                            alt="Question image"
                            className="mt-2 max-h-24 rounded-lg border border-white/[0.06] object-cover"
                          />
                        )}
                      </div>
                      {!isViewMode && (
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<ImagePlus className="w-3 h-3" />}
                            onClick={() => handleQuestionImageUpload(question.id)}
                            disabled={questionImageMutation.isPending && uploadingQuestionId === question.id}
                            className="text-teal-400 hover:text-teal-300 hover:bg-teal-500/10"
                          >
                            {questionImageMutation.isPending && uploadingQuestionId === question.id ? 'Uploading...' : 'Image'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Trash2 className="w-3 h-3" />}
                            onClick={() => handleRemoveQuestion(question.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Quick Actions */}
          {!isViewMode && (
            <Card className="p-4 bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border-teal-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">Quick Configuration</h3>
                  <p className="text-xs text-white/50">Enable or disable all optional settings at once</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        shuffle_questions: true,
                        shuffle_options: true,
                        show_results: true,
                        allow_review: true,
                        show_correct_answers: false,
                        auto_grade: true,
                        allow_retake: false,
                        allow_late_submit: false,
                        late_submit_penalty: 5,
                        auto_submit_on_timeout: true,
                        allow_backtracking: false,
                        require_answer_before_next: false,
                        require_fullscreen: true,
                        browser_lockdown: true,
                        proctoring_enabled: true,
                        proctoring_settings: {
                          ...formData.proctoring_settings,
                          webcam_required: true,
                          microphone_required: true,
                          screen_recording: true,
                          tab_switch_detection: true,
                          copy_paste_detection: true,
                          right_click_disabled: true,
                          keyboard_shortcuts_disabled: true,
                          face_detection: true,
                          multiple_faces_detection: true,
                          gaze_detection: true,
                          phone_detection: true,
                          vm_detection: true,
                        },
                        auto_penalty_enabled: true,
                        violation_penalties: {
                          tab_switch: { max_count: 5, action: 'warn' as const, penalty_percent: 5 },
                          copy_paste: { max_count: 5, action: 'penalty' as const, penalty_percent: 5 },
                          mobile_detected: { max_count: 5, action: 'auto_submit' as const, penalty_percent: 5 },
                          multiple_faces: { max_count: 5, action: 'auto_submit' as const, penalty_percent: 5 },
                          no_face: { max_count: 10, action: 'penalty' as const, penalty_percent: 5 },
                          devtools_open: { max_count: 5, action: 'auto_submit' as const, penalty_percent: 5 },
                          fullscreen_exit: { max_count: 5, action: 'warn' as const, penalty_percent: 5 },
                          looking_away: { max_count: 15, action: 'warn' as const, penalty_percent: 0 },
                          screenshot_attempt: { max_count: 5, action: 'auto_submit' as const, penalty_percent: 5 },
                        } as Record<string, { max_count: number; action: 'warn' | 'penalty' | 'auto_submit'; penalty_percent: number }>,
                        negative_marking: false,
                        partial_marking: false,
                        certificate_enabled: true,
                      });
                      toast.success('All recommended settings enabled');
                    }}
                  >
                    Enable All Recommended
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        shuffle_questions: false,
                        shuffle_options: false,
                        show_results: false,
                        allow_review: false,
                        show_correct_answers: false,
                        allow_retake: false,
                        allow_late_submit: false,
                        allow_backtracking: false,
                        require_answer_before_next: false,
                        require_fullscreen: false,
                        browser_lockdown: false,
                        ip_restriction_enabled: false,
                        proctoring_enabled: false,
                        proctoring_settings: {
                          ...formData.proctoring_settings,
                          webcam_required: false,
                          microphone_required: false,
                          screen_recording: false,
                          tab_switch_detection: false,
                          copy_paste_detection: false,
                          right_click_disabled: false,
                          keyboard_shortcuts_disabled: false,
                          face_detection: false,
                          multiple_faces_detection: false,
                          gaze_detection: false,
                          phone_detection: false,
                          vm_detection: false,
                        },
                        negative_marking: false,
                        partial_marking: false,
                        certificate_enabled: false,
                      });
                      toast.success('All optional settings disabled');
                    }}
                  >
                    Disable All Optional
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Attempts & Retakes */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-teal-500"></span>
              Attempts & Retakes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="block text-sm font-medium text-white/70">Maximum Attempts</label>
                  <HelpTip text="How many times a student can take this exam. Set to 1 for a single attempt." />
                </div>
                <Input
                  type="number"
                  value={formData.max_attempts}
                  onChange={(e) => setFormData({ ...formData, max_attempts: parseInt(e.target.value) || 1 })}
                  min="1"
                  disabled={isViewMode}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg h-fit">
                <div>
                  <p className="font-medium text-white text-sm">Allow Retake <HelpTip text="Let students retake the exam after completing it. They must wait for the retake delay period." /></p>
                  <p className="text-xs text-white/50">Students can retake after failing</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.allow_retake}
                  onChange={(e) => setFormData({ ...formData, allow_retake: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>
            </div>
            {formData.allow_retake && (
              <div className="mt-4">
                <div className="flex items-center gap-1 mb-1">
                  <label className="block text-sm font-medium text-white/70">Retake Delay (hours)</label>
                  <HelpTip text="Minimum hours a student must wait between attempts." />
                </div>
                <Input
                  type="number"
                  value={formData.retake_delay_hours}
                  onChange={(e) => setFormData({ ...formData, retake_delay_hours: parseInt(e.target.value) || 0 })}
                  min="0"
                  max="720"
                  disabled={isViewMode}
                />
                <p className="text-xs text-white/40 mt-1">Minimum hours before a student can retake</p>
              </div>
            )}
          </Card>

          {/* Results & Grading */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
              Results & Grading
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Show Results <HelpTip text="Let students see their exam results after submission." /></p>
                    <p className="text-xs text-white/50">Display scores to students</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.show_results}
                    onChange={(e) => setFormData({ ...formData, show_results: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Auto Grade <HelpTip text="Automatically grade MCQ and True/False questions on submission. Essay and short answer questions require manual grading." /></p>
                    <p className="text-xs text-white/50">Automatically grade MCQ/True-False</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.auto_grade}
                    onChange={(e) => setFormData({ ...formData, auto_grade: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
              </div>

              {formData.show_results && (
                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">Show Results Timing <HelpTip text="When to show results: immediately after submission, after exam end time, after all grading is complete, or only when manually released." /></label>
                  <select
                    value={formData.show_results_timing}
                    onChange={(e) => setFormData({ ...formData, show_results_timing: e.target.value as 'immediate' | 'after_end' | 'after_grading' | 'manual' })}
                    className="input-dark w-full"
                    disabled={isViewMode}
                  >
                    <option value="immediate">Immediately after submission</option>
                    <option value="after_end">After exam end time</option>
                    <option value="after_grading">After manual grading complete</option>
                    <option value="manual">Manually released by instructor</option>
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Allow Review</p>
                    <p className="text-xs text-white/50">Students can review their answers</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.allow_review}
                    onChange={(e) => setFormData({ ...formData, allow_review: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Show Correct Answers <HelpTip text="Display the correct answers alongside the student's answers in the results review." /></p>
                    <p className="text-xs text-white/50">Display correct answers in review</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.show_correct_answers}
                    onChange={(e) => setFormData({ ...formData, show_correct_answers: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
              </div>

              {/* Negative Marking */}
              <div className="border-t border-white/[0.06] pt-4 mt-4">
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Negative Marking <HelpTip text="Deduct marks for wrong answers. Only applies to MCQ and True/False questions." /></p>
                    <p className="text-xs text-white/50">Deduct marks for wrong answers</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.negative_marking}
                    onChange={(e) => setFormData({ ...formData, negative_marking: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
                {formData.negative_marking && (
                  <div className="mt-3 ml-4">
                    <div className="flex items-center gap-1 mb-1">
                      <label className="block text-sm font-medium text-white/70">Negative Marking Percentage</label>
                      <HelpTip text="Percentage of the question's points to deduct for a wrong answer. E.g., 25% means a 4-point question deducts 1 point." />
                    </div>
                    <Input
                      type="number"
                      value={formData.negative_marking_percentage}
                      onChange={(e) => setFormData({ ...formData, negative_marking_percentage: parseInt(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      disabled={isViewMode}
                    />
                    <p className="text-xs text-white/40 mt-1">Percentage of marks deducted for wrong answers (e.g., 25% = -0.25 for 1 mark question)</p>
                  </div>
                )}
              </div>

              {/* Custom Messages */}
              <div className="border-t border-white/[0.06] pt-4 mt-4">
                <h3 className="text-sm font-medium text-white/70 mb-3">Custom Result Messages</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Pass Message <HelpTip text="Custom message shown to students who pass the exam." /></label>
                    <input
                      type="text"
                      value={formData.pass_message}
                      onChange={(e) => setFormData({ ...formData, pass_message: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Congratulations! You have passed the exam."
                      disabled={isViewMode}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Fail Message <HelpTip text="Custom message shown to students who fail the exam." /></label>
                    <input
                      type="text"
                      value={formData.fail_message}
                      onChange={(e) => setFormData({ ...formData, fail_message: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Unfortunately, you did not pass. Please review and try again."
                      disabled={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Question & Navigation Settings */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-purple-500"></span>
              Question & Navigation
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Shuffle Questions <HelpTip text="Randomize the order of questions for each student. Prevents copying from neighbors." /></p>
                  <p className="text-xs text-white/50">Randomize question order</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.shuffle_questions}
                  onChange={(e) => setFormData({ ...formData, shuffle_questions: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Shuffle Options <HelpTip text="Randomize the order of MCQ answer choices for each student." /></p>
                  <p className="text-xs text-white/50">Randomize answer choices</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.shuffle_options}
                  onChange={(e) => setFormData({ ...formData, shuffle_options: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Allow Backtracking <HelpTip text="Let students go back to previous questions. Disable for sequential-only exams." /></p>
                  <p className="text-xs text-white/50">Navigate to previous questions</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.allow_backtracking}
                  onChange={(e) => setFormData({ ...formData, allow_backtracking: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Require Answer <HelpTip text="Students must answer the current question before moving to the next one." /></p>
                  <p className="text-xs text-white/50">Must answer before next question</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.require_answer_before_next}
                  onChange={(e) => setFormData({ ...formData, require_answer_before_next: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Allow Skip <HelpTip text="Let students skip ahead to any question. Disable to force sequential answering." /></p>
                  <p className="text-xs text-white/50">Skip questions and return later</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.allow_skip}
                  onChange={(e) => setFormData({ ...formData, allow_skip: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Question Navigator <HelpTip text="Show the numbered question navigator panel on the side during the exam." /></p>
                  <p className="text-xs text-white/50">Show numbered question navigator panel</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.show_question_navigator}
                  onChange={(e) => setFormData({ ...formData, show_question_navigator: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>
            </div>
          </Card>

          {/* Submission Settings */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Submission Settings
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Auto-Submit on Timeout <HelpTip text="Automatically submit the exam when the timer runs out. If disabled, students get a warning but can still submit manually." /></p>
                    <p className="text-xs text-white/50">Submit automatically when time runs out</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.auto_submit_on_timeout}
                    onChange={(e) => setFormData({ ...formData, auto_submit_on_timeout: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Allow Late Submission <HelpTip text="Accept exam submissions after the scheduled end time." /></p>
                    <p className="text-xs text-white/50">Accept submissions after end time</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.allow_late_submit}
                    onChange={(e) => setFormData({ ...formData, allow_late_submit: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
              </div>
              {formData.allow_late_submit && (
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-sm font-medium text-white/70">Late Submission Penalty (%)</label>
                    <HelpTip text="Percentage deducted from the score for late submissions." />
                  </div>
                  <Input
                    type="number"
                    value={formData.late_submit_penalty}
                    onChange={(e) => setFormData({ ...formData, late_submit_penalty: parseInt(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    disabled={isViewMode}
                  />
                  <p className="text-xs text-white/40 mt-1">Percentage deducted from score for late submissions</p>
                </div>
              )}
            </div>
          </Card>

          {/* Security Settings */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              Security & Access Control
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Require Fullscreen <HelpTip text="Force the exam to run in fullscreen mode. Exam pauses if the student exits fullscreen." /></p>
                    <p className="text-xs text-white/50">Force fullscreen mode during exam</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.require_fullscreen}
                    onChange={(e) => setFormData({ ...formData, require_fullscreen: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Browser Lockdown <HelpTip text="Block right-click, keyboard shortcuts (Ctrl+C, Alt+Tab), and developer tools during the exam." /></p>
                    <p className="text-xs text-white/50">Restrict browser functionality</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.browser_lockdown}
                    onChange={(e) => setFormData({ ...formData, browser_lockdown: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
              </div>

              {/* IP Restriction */}
              <div className="border-t border-white/[0.06] pt-4">
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">IP Restriction <HelpTip text="Only allow exam access from specific IP addresses or ranges." /></p>
                    <p className="text-xs text-white/50">Limit access to specific IP addresses</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.ip_restriction_enabled}
                    onChange={(e) => setFormData({ ...formData, ip_restriction_enabled: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>
                {formData.ip_restriction_enabled && (
                  <div className="mt-3">
                    <label className="block text-xs text-white/50 mb-1">Allowed IP Ranges (comma separated)</label>
                    <input
                      type="text"
                      value={formData.allowed_ip_ranges}
                      onChange={(e) => setFormData({ ...formData, allowed_ip_ranges: e.target.value })}
                      className="input-dark w-full"
                      placeholder="192.168.1.0/24, 10.0.0.0/8"
                      disabled={isViewMode}
                    />
                    <p className="text-xs text-white/40 mt-1">Enter CIDR notation or specific IPs</p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Proctoring Settings */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              Proctoring
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                <div>
                  <p className="font-medium text-white">Enable Proctoring <HelpTip text="Activate AI-powered exam monitoring with webcam, screen, and behavior analysis." /></p>
                  <p className="text-sm text-white/50">Monitor students during the exam with AI-powered detection</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.proctoring_enabled}
                  onChange={(e) => setFormData({ ...formData, proctoring_enabled: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>

              {formData.proctoring_enabled && (
                <div className="space-y-6 border-l-2 border-emerald-500/30 pl-4 ml-2">
                  {/* Device Requirements */}
                  <div>
                    <h3 className="text-sm font-medium text-white/70 mb-3">Device Requirements</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.webcam_required}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, webcam_required: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Webcam Required <HelpTip text="Require students to enable their webcam throughout the exam for face detection." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.microphone_required}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, microphone_required: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Microphone Required <HelpTip text="Require microphone access for audio monitoring during the exam." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.screen_recording}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, screen_recording: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Screen Recording <HelpTip text="Record the student's screen during the exam for later review." /></span>
                      </label>
                    </div>
                  </div>

                  {/* Browser Monitoring */}
                  <div>
                    <h3 className="text-sm font-medium text-white/70 mb-3">Browser Monitoring</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.tab_switch_detection}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, tab_switch_detection: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Tab Switch Detection <HelpTip text="Detect and log when students switch to other browser tabs or windows." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.copy_paste_detection}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, copy_paste_detection: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Copy/Paste Detection <HelpTip text="Detect and block copy, cut, and paste operations during the exam." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.right_click_disabled}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, right_click_disabled: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Right-Click Disabled <HelpTip text="Disable the right-click context menu to prevent copying content." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.keyboard_shortcuts_disabled}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, keyboard_shortcuts_disabled: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Keyboard Shortcuts Disabled <HelpTip text="Block common keyboard shortcuts like Ctrl+C, Ctrl+V, Alt+Tab, PrintScreen." /></span>
                      </label>
                    </div>
                  </div>

                  {/* AI Detection */}
                  <div>
                    <h3 className="text-sm font-medium text-white/70 mb-3">AI Detection</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.face_detection}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, face_detection: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Face Detection <HelpTip text="Use AI to detect if a face is present. Logs a violation when no face is detected." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.multiple_faces_detection}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, multiple_faces_detection: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Multiple Faces Detection <HelpTip text="Detect if more than one person is visible on camera." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.gaze_detection}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, gaze_detection: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Gaze Detection <HelpTip text="Track eye movement to detect if the student is looking away from the screen." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.phone_detection}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, phone_detection: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>Phone Detection <HelpTip text="Use AI to detect mobile phones or prohibited objects in the camera frame." /></span>
                      </label>
                      <label className="flex items-center space-x-2 text-sm text-white/70">
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.vm_detection}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, vm_detection: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                        <span>VM Detection <HelpTip text="Detect if the exam is being taken inside a virtual machine." /></span>
                      </label>
                    </div>
                  </div>

                  {/* Violation Settings */}
                  <div>
                    <h3 className="text-sm font-medium text-white/70 mb-3">Violation Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <label className="block text-sm font-medium text-white/70">Warning Threshold</label>
                          <HelpTip text="Global warning: After this many TOTAL violations (all types combined), the student sees a general warning popup. Works alongside per-type thresholds below — whichever triggers first." />
                        </div>
                        <Input
                          type="number"
                          value={formData.proctoring_settings.warning_threshold}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, warning_threshold: parseInt(e.target.value) || 5 }})}
                          min="1"
                          max="10"
                          disabled={isViewMode}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <label className="block text-sm font-medium text-white/70">Max Violations</label>
                          <HelpTip text="Global safety net: After this many TOTAL violations (all types combined), the exam is auto-terminated — even if no single violation type hit its individual limit. Set this higher than your per-type limits (e.g., 15-20) as a catch-all." />
                        </div>
                        <Input
                          type="number"
                          value={formData.proctoring_settings.max_violations}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, max_violations: parseInt(e.target.value) || 25 }})}
                          min="1"
                          max="100"
                          disabled={isViewMode}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg h-fit">
                        <div>
                          <p className="font-medium text-white text-sm">Auto-Terminate <HelpTip text="Automatically end the exam when max violations is reached." /></p>
                          <p className="text-xs text-white/50">End exam on max violations</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.proctoring_settings.auto_terminate}
                          onChange={(e) => setFormData({ ...formData, proctoring_settings: { ...formData.proctoring_settings, auto_terminate: e.target.checked }})}
                          className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-white/40 mt-2">Warning appears at threshold, exam terminates at max violations</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Violation Penalties */}
          <Card className="p-6">
            {/* Section header — always visible, acts as the collapse toggle */}
            <button
              type="button"
              className="w-full flex items-center justify-between"
              onClick={() => setViolationPenaltiesExpanded((v) => !v)}
            >
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                Violation Penalties <HelpTip text="Configure what happens when students commit violations. Max Count sets the limit, Action determines the response (Warn = just warn, Penalty = deduct marks, Auto Submit = end the exam), and Penalty % sets how much to deduct per occurrence." />
              </h2>
              {violationPenaltiesExpanded ? (
                <ChevronDown className="w-4 h-4 text-white/50" />
              ) : (
                <ChevronRight className="w-4 h-4 text-white/50" />
              )}
            </button>

            {violationPenaltiesExpanded && (
              <div className="mt-4 space-y-4">
                {/* Master toggle */}
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Enable Auto-Penalty <HelpTip text="Automatically deduct marks from the student's score when a violation is detected. Configure penalty per violation type below." /></p>
                    <p className="text-xs text-white/50">Automatically deduct marks when a violation is detected</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.auto_penalty_enabled}
                    onChange={(e) => setFormData({ ...formData, auto_penalty_enabled: e.target.checked })}
                    className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                    disabled={isViewMode}
                  />
                </div>

                {formData.auto_penalty_enabled && (
                  <div className="space-y-3 border-l-2 border-orange-500/30 pl-4 ml-2">
                    {/* Column headers */}
                    <div className="grid grid-cols-[1fr_80px_120px_80px] gap-3 px-3 pb-1 border-b border-white/[0.06]">
                      <span className="text-xs font-medium text-white/40 uppercase tracking-wide">Violation Type</span>
                      <span className="text-xs font-medium text-white/40 uppercase tracking-wide text-center">Max Count</span>
                      <span className="text-xs font-medium text-white/40 uppercase tracking-wide text-center">Action</span>
                      <span className="text-xs font-medium text-white/40 uppercase tracking-wide text-right">Penalty (%)</span>
                    </div>

                    {/* Per-violation rows */}
                    {Object.entries(DEFAULT_VIOLATION_PENALTIES).map(([key, meta]) => (
                      <div
                        key={key}
                        className="grid grid-cols-[1fr_80px_120px_80px] items-center gap-3 p-3 bg-white/[0.03] border border-white/[0.06] rounded-lg"
                      >
                        <span className="text-sm text-white/70">{meta.label}</span>
                        {/* Max Count */}
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={formData.violation_penalties[key]?.max_count ?? meta.max_count}
                          onChange={(e) => {
                            const val = Math.max(1, parseInt(e.target.value) || 1);
                            setFormData({
                              ...formData,
                              violation_penalties: {
                                ...formData.violation_penalties,
                                [key]: { ...formData.violation_penalties[key], max_count: val },
                              },
                            });
                          }}
                          className="w-full text-center bg-white/[0.06] border border-white/[0.10] rounded-md px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-teal-500/60 disabled:opacity-50"
                          disabled={isViewMode}
                        />
                        {/* Action */}
                        <select
                          value={formData.violation_penalties[key]?.action ?? meta.action}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              violation_penalties: {
                                ...formData.violation_penalties,
                                [key]: { ...formData.violation_penalties[key], action: e.target.value as 'warn' | 'penalty' | 'auto_submit' },
                              },
                            });
                          }}
                          className="w-full bg-white/[0.06] border border-white/[0.10] rounded-md px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-teal-500/60 disabled:opacity-50"
                          disabled={isViewMode}
                        >
                          <option value="warn">Warn</option>
                          <option value="penalty">Penalty</option>
                          <option value="auto_submit">Auto Submit</option>
                        </select>
                        {/* Penalty % */}
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.violation_penalties[key]?.penalty_percent ?? meta.penalty_percent}
                            onChange={(e) => {
                              const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                              setFormData({
                                ...formData,
                                violation_penalties: {
                                  ...formData.violation_penalties,
                                  [key]: { ...formData.violation_penalties[key], penalty_percent: val },
                                },
                              });
                            }}
                            className="w-full text-right bg-white/[0.06] border border-white/[0.10] rounded-md px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-teal-500/60 disabled:opacity-50"
                            disabled={isViewMode}
                          />
                          <span className="text-sm text-white/40">%</span>
                        </div>
                      </div>
                    ))}

                    {/* Reset to defaults button */}
                    {!isViewMode && (
                      <div className="flex justify-end pt-1">
                        <button
                          type="button"
                          className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
                          onClick={() => {
                            const defaults: Record<string, { max_count: number; action: 'warn' | 'penalty' | 'auto_submit'; penalty_percent: number }> = {};
                            Object.entries(DEFAULT_VIOLATION_PENALTIES).forEach(([k, v]) => {
                              defaults[k] = { max_count: v.max_count, action: v.action, penalty_percent: v.penalty_percent };
                            });
                            setFormData({ ...formData, violation_penalties: defaults });
                          }}
                        >
                          <RotateCcw className="w-3 h-3" />
                          Reset to Defaults
                        </button>
                      </div>
                    )}

                    <p className="text-xs text-white/40 pt-1">
                      Each penalty is deducted from the student's total score as a percentage of total marks. Multiple violations of the same type stack.
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Plagiarism Settings */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
              Plagiarism
            </h2>
            <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
              <div>
                <p className="font-medium text-white text-sm">Auto Plagiarism Check <HelpTip text="Automatically run plagiarism detection on essay and short answer responses after submission." /></p>
                <p className="text-xs text-white/50">Automatically analyze for plagiarism when students submit</p>
              </div>
              <input
                type="checkbox"
                checked={formData.auto_plagiarism_check}
                onChange={(e) => setFormData({ ...formData, auto_plagiarism_check: e.target.checked })}
                className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                disabled={isViewMode}
              />
            </div>
          </Card>

          {/* Certificate Settings */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              Certificate
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/[0.06] rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Certificate Enabled <HelpTip text="Automatically generate a certificate when a student passes the exam." /></p>
                  <p className="text-xs text-white/50">Generate certificates for students who pass</p>
                </div>
                <input
                  type="checkbox"
                  checked={formData.certificate_enabled}
                  onChange={(e) => setFormData({ ...formData, certificate_enabled: e.target.checked })}
                  className="w-5 h-5 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer checked:bg-teal-500 checked:border-teal-500"
                  disabled={isViewMode}
                />
              </div>
              {formData.certificate_enabled && (
                <div className="space-y-4 p-4 bg-white/5 border border-white/[0.06] rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1">Certificate Title</label>
                      <Input
                        value={formData.certificate_config.title}
                        onChange={(e) => setFormData({ ...formData, certificate_config: { ...formData.certificate_config, title: e.target.value } })}
                        placeholder="e.g., Certificate of Completion"
                        disabled={isViewMode}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1">Subtitle</label>
                      <Input
                        value={formData.certificate_config.subtitle}
                        onChange={(e) => setFormData({ ...formData, certificate_config: { ...formData.certificate_config, subtitle: e.target.value } })}
                        placeholder="e.g., For outstanding performance"
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1">Signature Name</label>
                      <Input
                        value={formData.certificate_config.signature_name}
                        onChange={(e) => setFormData({ ...formData, certificate_config: { ...formData.certificate_config, signature_name: e.target.value } })}
                        placeholder="e.g., Dr. John Smith"
                        disabled={isViewMode}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-1">Signature Title</label>
                      <Input
                        value={formData.certificate_config.signature_title}
                        onChange={(e) => setFormData({ ...formData, certificate_config: { ...formData.certificate_config, signature_title: e.target.value } })}
                        placeholder="e.g., Head of Department"
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-1">Organization Name</label>
                    <Input
                      value={formData.certificate_config.organization_name}
                      onChange={(e) => setFormData({ ...formData, certificate_config: { ...formData.certificate_config, organization_name: e.target.value } })}
                      placeholder="e.g., CyberShield Academy"
                      disabled={isViewMode}
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.certificate_config.show_grade}
                        onChange={(e) => setFormData({ ...formData, certificate_config: { ...formData.certificate_config, show_grade: e.target.checked } })}
                        className="w-4 h-4 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer"
                        disabled={isViewMode}
                      />
                      <span className="text-sm text-white/70">Show Grade</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.certificate_config.show_score}
                        onChange={(e) => setFormData({ ...formData, certificate_config: { ...formData.certificate_config, show_score: e.target.checked } })}
                        className="w-4 h-4 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer"
                        disabled={isViewMode}
                      />
                      <span className="text-sm text-white/70">Show Score</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                      <input
                        type="checkbox"
                        checked={formData.certificate_config.anchor_to_blockchain}
                        onChange={(e) => setFormData({ ...formData, certificate_config: { ...formData.certificate_config, anchor_to_blockchain: e.target.checked } })}
                        className="w-4 h-4 accent-teal-500 bg-white/10 border-2 border-white/40 rounded cursor-pointer"
                        disabled={isViewMode}
                      />
                      <span className="text-sm text-white/70">Blockchain Anchor</span>
                    </div>
                  </div>
                  <p className="text-xs text-white/40">Leave title blank to use the default: "Certificate of Completion - [Exam Title]"</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

    </div>
  );
};
